import { RefObject, useEffect } from 'react'

import { CustomEvents } from 'shared/constants/customEvents'
import { WindowState } from 'shared/types'

/**
 * Hook that listens for ticket events via postMessage
 * and relays to task-management iframe (Detached Mode) for query invalidation.
 *
 * Modal events (openTicketTrackModal, openTicketModal) are handled by their
 * respective modal components directly - no relay needed.
 */
export const useTicketCRUDListener = (
  iframeRef: RefObject<HTMLIFrameElement>,
  window: WindowState
) => {
  const isTaskManagementWindow = window.id?.includes('task-management')

  useEffect(() => {
    if (!isTaskManagementWindow) return

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== globalThis.window.location.origin) return

      const { type } = event.data || {}

      // Relay ticket events to iframe for query invalidation
      if (
        type === CustomEvents.TICKET_CREATED ||
        type === CustomEvents.TICKET_DELETED
      ) {
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            event.data,
            globalThis.window.location.origin
          )
        }
      }
    }

    globalThis.window.addEventListener('message', handleMessage)

    return () => {
      globalThis.window.removeEventListener('message', handleMessage)
    }
  }, [iframeRef, isTaskManagementWindow])
}
